import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCircle, Mail, Shield, Phone, Save } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import type { User } from '../../types';

export const MaintenanceAccount: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState((user as User | null | undefined)?.phone ?? '');
  const [mutationError, setMutationError] = useState<string | null>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['my-user-profile'],
    queryFn: () => api.getMyUserProfile(),
    staleTime: 60_000,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { name: string; phone?: string }) =>
      api.updateUser(user!.id, { name: payload.name, phone: payload.phone }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-user-profile'] });
      setMutationError(null);
    },
    onError: (err: Error) => setMutationError(err.message || 'Error al actualizar el perfil'),
  });

  const effectiveName = profile?.name ?? name ?? user?.name ?? '';
  const effectiveEmail = profile?.email ?? user?.email ?? '';
  const effectivePhone = profile?.phone ?? phone ?? '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setMutationError('El nombre no puede estar vacío.');
      return;
    }
    updateMutation.mutate({ name: trimmedName, phone: phone.trim() || undefined });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
        <UserCircle className="w-7 h-7 text-primary" />
        Mi cuenta
      </h1>

      <Card title="Datos personales">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {mutationError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {mutationError}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                    <UserCircle className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25326B] focus:border-transparent text-sm"
                    placeholder="Tu nombre"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                    <Mail className="w-4 h-4" />
                  </span>
                  <input
                    type="email"
                    value={effectiveEmail}
                    disabled
                    className="block w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                    <Phone className="w-4 h-4" />
                  </span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25326B] focus:border-transparent text-sm"
                    placeholder="Tu número de contacto"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rol / Puesto</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                    <Shield className="w-4 h-4" />
                  </span>
                  <input
                    type="text"
                    value={user?.role?.name || ''}
                    disabled
                    className="block w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white"
                style={{ backgroundColor: '#25326B' }}
                disabled={updateMutation.isPending}
              >
                <Save className="w-4 h-4 mr-2" />
                {updateMutation.isPending ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

